import { useNavigate } from "@tanstack/react-router"
import type { ReactNode } from "react"

import { formatWeekRange } from "../directories/staff/schedule-model"
import { formatTimeOfDay, shiftDate } from "../../lib/format"
import { ProblemScreen, Skeleton } from "../../shell/states"
import { useOnline } from "../../shell/use-online"
import { Button, EmptyState } from "../../ui"
import { MasterFrame, MasterHead, MasterNoEmployee } from "./master-frame"
import type { MasterWeekDay } from "./master-week"
import {
  dayDetail,
  dayLine,
  dayTag,
  SCHEDULE_OWNER_NOTE,
  weekSummary,
} from "./master-week"
import styles from "./master-week-screen.module.css"
import type { MasterSearch } from "./use-master-day"
import { useMasterWeek } from "./use-master-week"

/**
 * Строка дня.
 *
 * ОДНА ТОЧКА — НАЖИМАЕТСЯ ВСЯ СТРОКА: на телефоне это главный способ открыть день,
 * и отбирать его ради редкого случая нельзя. ДВЕ ТОЧКИ — СТРОКА СПРАШИВАЕТ, КУДА:
 * молча открыть один из двух филиалов значит показать мастеру чужой день и сказать
 * ему «выходной» про день, в который он работает.
 */
function DayRow(props: {
  readonly day: MasterWeekDay
  readonly network: boolean
  readonly partial: boolean
  readonly onOpen: (branchId: string | null) => void
}): ReactNode {
  const { day } = props
  const tag = dayTag(day)
  const detail = dayDetail(day)
  const off = day.shifts.length === 0
  const many = day.targets.length > 1

  const inside = (
    <>
      <span className={styles["dayHead"]}>
        <span className={off ? styles["dayNameOff"] : styles["dayName"]}>{day.label}</span>
        <span className={styles["dayLine"]}>{dayLine(day, props.network, props.partial)}</span>
        {tag === null ? null : (
          <span className={tag === "today" ? styles["tagToday"] : styles["tagTight"]}>
            {tag === "today" ? "сегодня" : "плотно"}
          </span>
        )}
      </span>

      {off ? null : (
        <span
          className={styles["bar"]}
          role="progressbar"
          aria-valuenow={day.percent}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Загрузка дня ${day.label}`}
        >
          <span className={styles["barFill"]} style={{ width: `${String(day.percent)}%` }} />
        </span>
      )}

      {detail === null ? null : <span className={styles["detail"]}>{detail}</span>}
    </>
  )

  if (!many) {
    return (
      <button
        type="button"
        className={off ? styles["dayOff"] : styles["day"]}
        onClick={() => {
          props.onOpen(day.targets[0]?.branchId ?? null)
        }}
      >
        {inside}
      </button>
    )
  }

  return (
    <div className={styles["dayRow"]}>
      {inside}
      <span className={styles["targets"]}>
        {day.targets.map((target) => (
          <Button
            key={target.branchId}
            size="sm"
            variant="secondary"
            onClick={() => {
              props.onOpen(target.branchId)
            }}
          >
            Открыть: {target.branchName}
          </Button>
        ))}
      </span>
    </div>
  )
}

/**
 * 5-М2 «Моя неделя» — /my/week.
 *
 * ЗАЧЕМ МАСТЕРУ ЭТОТ ЭКРАН. Он отвечает на два вопроса, которые мастер задаёт себе
 * вечером и утром: когда я работаю на этой неделе и насколько забит день. Первый —
 * чтобы не прийти в выходной и не прогулять смену; второй — чтобы знать, выдержит
 * ли день опоздание клиента.
 *
 * СПИСОК, А НЕ СЕТКА ПО ЧАСАМ. На 360 px семь колонок по получасам дают клетки в 6 px:
 * ни попасть пальцем, ни прочитать. Мастер и не двигает записи — график ставит
 * администратор, — поэтому день здесь строка: смена, число записей и полоса загрузки.
 *
 * ПРАВИТЬ ГРАФИК ОТСЮДА НЕЛЬЗЯ, И ЭТО СКАЗАНО СЛОВАМИ. Отсутствие кнопки человек
 * читает как «не нашёл», а не как «не положено»: строка внизу объясняет, к кому идти.
 */
export function MasterWeekScreen(props: { readonly search: MasterSearch }): ReactNode {
  const navigate = useNavigate()
  const data = useMasterWeek(props.search)
  const online = useOnline()
  const network = data.branches.length > 1

  const goWeek = (delta: number): void => {
    void navigate({ to: "/my/week", search: { date: shiftDate(data.start, delta * 7) } })
  }

  // Человек не заведён мастером: своей недели у него нет и быть не может.
  if (data.employeeId === null) {
    return (
      <MasterFrame active="week">
        <MasterNoEmployee title="Моя неделя" />
      </MasterFrame>
    )
  }

  if (data.branches.length === 0) {
    /*
     * ПРИЧИНА НАЗЫВАЕТСЯ ВЕРНО. «Вам не открыт ни один филиал» посылает мастера
     * просить доступ, которого у него и так достаточно: точки выключены владельцем,
     * и это другой разговор, а неверно названная причина стоит человеку звонка.
     */
    return (
      <MasterFrame active="week">
        <MasterHead title="Моя неделя" />
        {data.disabledBranches.length > 0 ? (
          <EmptyState
            title="Ваши филиалы выключены"
            text={`${data.disabledBranches.join(", ")} — работа в этих точках приостановлена владельцем салона. Пока точку не включат, смен и записей в ней не будет.`}
          />
        ) : (
          <EmptyState
            title="Вам не открыт ни один филиал"
            text="Доступ к филиалу выдаёт владелец или управляющий салона. Расписание появится сразу после этого."
          />
        )}
      </MasterFrame>
    )
  }

  // Отказ ДО первых данных — отдельный экран. Отказ одного филиала из двух им
  // не является: неделя показывается, а непришедшая точка называется словами.
  if (data.problem !== null && data.view === null) {
    return (
      <MasterFrame active="week">
        <ProblemScreen problem={data.problem} onRetry={data.reload} />
      </MasterFrame>
    )
  }

  const arrows = (
    <>
      <Button size="sm" variant="secondary" aria-label="Предыдущая неделя" onClick={() => goWeek(-1)}>
        ‹
      </Button>
      <Button size="sm" variant="secondary" aria-label="Следующая неделя" onClick={() => goWeek(1)}>
        ›
      </Button>
      {/* 5-М6: отпроситься — со своей недели, где видно, какие дни просишь. */}
      <Button size="sm" variant="secondary" onClick={() => void navigate({ to: "/my/time-off" })}>
        Отпроситься
      </Button>
    </>
  )

  return (
    <MasterFrame active="week">
      <MasterHead
        title="Моя неделя"
        subtitle={
          data.view === null
            ? formatWeekRange(data.start)
            : `${formatWeekRange(data.start)} · ${weekSummary(data.view)}`
        }
        actions={arrows}
      />

      {online || data.generatedAt === null ? null : (
        // В офлайне мастер должен знать, чему он верит: неделя могла загрузиться утром.
        <div className={styles["stale"]} role="status">
          Нет связи. Показываю неделю, загруженную в{" "}
          {formatTimeOfDay(data.generatedAt, data.timeZone)}
        </div>
      )}

      {data.disabledBranches.length === 0 ? null : (
        // Смены мастера в выключенной точке в графике остаются: промолчать о точке
        // значит показать эти дни выходными и не сказать, почему.
        <div className={styles["stale"]} role="status">
          {data.disabledBranches.join(", ")} выключен: смены и записи этой точки
          в неделю не попали.
        </div>
      )}

      {data.failedBranches.length > 0 ? (
        <div className={styles["partial"]} role="status">
          <span>
            {data.failedBranches.join(", ")} не ответил: смены этой точки в неделе не
            показаны. Остальное актуально.
          </span>
          <Button size="sm" variant="secondary" onClick={data.reload}>
            Повторить
          </Button>
        </div>
      ) : null}

      {data.loading && data.view === null ? (
        <div className={styles["rows"]} aria-busy="true" aria-live="polite">
          {[0, 1, 2, 3, 4, 5, 6].map((row) => (
            <Skeleton key={row} width="100%" height={64} radius={12} />
          ))}
        </div>
      ) : data.view === null ? null : data.view.noShifts ? (
        <EmptyState
          title="На этой неделе смен нет"
          text={`Смены на неделю ставит администратор салона. ${SCHEDULE_OWNER_NOTE}`}
        />
      ) : (
        <>
          <div className={styles["rows"]}>
            {data.view.days.map((day) => (
              <DayRow
                key={day.date}
                day={day}
                network={network}
                partial={data.view?.partial ?? false}
                onOpen={(branchId) => {
                  /*
                   * ТОЧКА ПЕРЕДАЁТСЯ ВМЕСТЕ С ДАТОЙ. «Мой день» берёт филиал из адреса,
                   * а без него подставляет запомненный: пятничная смена «на Гагарина»
                   * открывалась днём «на Ленина» и читалась там «у вас выходной».
                   */
                  void navigate({
                    to: "/my/day",
                    search: branchId === null ? { date: day.date } : { date: day.date, branch: branchId },
                  })
                }}
              />
            ))}
          </div>

          {/*
            * «Запросить отсутствие» на листе — ссылка в этой фразе, но экрана запроса
            * в продукте пока нет (решение владельца по вопросу 42). Ссылка без экрана
            * хуже её отсутствия: человек нажмёт и ничего не произойдёт. Поэтому фраза
            * есть, а ссылки нет — и сказано, к кому идти вместо неё.
            */}
          <p className={styles["note"]}>{SCHEDULE_OWNER_NOTE}</p>
        </>
      )}
    </MasterFrame>
  )
}
